import { fieldSurface } from '../field-focus'
import { labelContent } from './labelContent'
import { useId, useState } from 'react'
import { RadioGroup } from './Checkbox'

export type DatePrecision = 'day' | 'month' | 'year'
export interface DateValue { value: string; precision: DatePrecision }

interface DatePickerProps {
  label?: string
  value: DateValue | string | undefined
  onChange: (v: DateValue) => void
  /** Show the Day / Month / Year precision toggle (spec Appendix C). */
  precision?: boolean
  error?: string
  help?: string
  disabled?: boolean
  id?: string
}

function normalise(v: DateValue | string | undefined): DateValue {
  if (!v) return { value: '', precision: 'day' }
  if (typeof v === 'string') return { value: v, precision: 'day' }
  return v
}

const INPUT_TYPE: Record<DatePrecision, string> = { day: 'date', month: 'month', year: 'number' }

export function DatePicker({ label, value, onChange, precision = false, error, help, disabled, id }: DatePickerProps) {
  const [focused, setFocused] = useState(false)
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const current = normalise(value)
  const { borderColor, backgroundColor } = fieldSurface({ focused, error: Boolean(error) })

  const setPrecision = (p: DatePrecision) => {
    // Truncate the stored value to the new precision so it stays parseable.
    const parts = current.value.split('-')
    const next = p === 'year' ? (parts[0] ?? '') : p === 'month' ? parts.slice(0, 2).join('-') : current.value
    onChange({ value: next, precision: p })
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {label && (
        <label htmlFor={fieldId} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>{labelContent(label)}</label>
      )}
      <div className="flex items-center" style={{ gap: 10 }}>
        <input
          id={fieldId}
          type={INPUT_TYPE[current.precision]}
          value={current.value}
          min={current.precision === 'year' ? 1800 : undefined}
          max={current.precision === 'year' ? 2100 : undefined}
          placeholder={current.precision === 'year' ? 'YYYY' : undefined}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange({ value: e.target.value, precision: current.precision })}
          className="font-jakarta text-text-input outline-none"
          style={{
            height: 40, backgroundColor, border: `0.5px solid ${borderColor}`, padding: '0 12px',
            fontSize: 12, letterSpacing: '-0.12px', colorScheme: 'dark', width: current.precision === 'year' ? 120 : 200,
            opacity: disabled ? 0.6 : 1, transition: 'background-color 0.15s ease',
          }}
        />
        {precision && (
          <RadioGroup
            direction="row"
            value={current.precision}
            onChange={(p) => setPrecision(p as DatePrecision)}
            options={[{ value: 'day', label: 'Day' }, { value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }]}
            disabled={disabled}
          />
        )}
      </div>
      {(error ?? help) && (
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: error ? '#F05042' : '#969696' }}>
          {error ?? help}
        </span>
      )}
    </div>
  )
}
